import { createReducer } from '@reduxjs/toolkit';
import {
  NotificationGetPersistedSucceededPayloadT,
  NotificationHistoryType,
  NotificationsState,
  NotificationT,
  PersistedNotificationResponseT,
  PersistedNotificationT,
} from 'lib-shared-legacy-types';
import merge from 'lodash/merge';
import omit from 'lodash/omit';


import { notificationActions } from '../actions/notification';

const {
  notificationDesktopPermissionSucceeded,
  notificationDesktopPermissionFailed,
  notificationAdded,
  notificationRemoveInStateById,
  notificationRemoveAllById,
  notificationRemoveInStateByType,
  notificationRatingAdded,
  notificationHistoryReset,
  notificationHistoryAdd,
  notificationTabBadgeShow,
  notificationTabBadgeReset,
  notificationGetPersistedSucceeded,
  notificationUpdate,
} = notificationActions;

export type NotificationType = NotificationsState;

// Init state for the Notification.
export const initialState: NotificationType = {
  ui: {
    showBadge: false,
  },
  meta: {
    desktopPermission: false,
  },
  currentNotifications: [],
  notificationsHistory: [],
  persistedNotifications: [],
  ratingNotificaions: [],
};

const notifications = createReducer(initialState, (builder) => {
  builder
    .addCase(notificationUpdate, (state, action) => {
      const {
        patch: { updates, deletes },
        id,
      } = action.payload;
      const notificationIndex = state.currentNotifications.findIndex(
        (currentNotification) => currentNotification.id === id,
      );

      if (notificationIndex === -1) {
        return;
      }

      if (updates) {
        const { time, ...restUpdates } = updates;
        state.currentNotifications[notificationIndex] = merge(
          state.currentNotifications[notificationIndex],
          restUpdates,
        );

        if (time) {
          const unixTime = new Date().getTime();
          state.currentNotifications[notificationIndex] = merge(
            {},
            state.currentNotifications[notificationIndex],
            {
              startTime: time,
              shouldBeRemovedAt: unixTime + time,
            },
          );
        }
      }

      if (deletes) {
        state.currentNotifications[notificationIndex] = omit(
          state.currentNotifications[notificationIndex],
          deletes,
        ) as (typeof state.currentNotifications)[number];
      }
    })
    .addCase(
      notificationTabBadgeShow.toString(),
      (state: NotificationsState) => {
        return { ...state, ui: { ...state.ui, showBadge: true } };
      },
    )
    .addCase(
      notificationTabBadgeReset.toString(),
      (state: NotificationsState) => {
        return { ...state, ui: { ...state.ui, showBadge: false } };
      },
    )
    .addCase(
      notificationDesktopPermissionSucceeded.toString(),
      (state: NotificationsState) => {
        return { ...state, meta: { ...state.meta, desktopPermission: true } };
      },
    )
    .addCase(
      notificationDesktopPermissionFailed.toString(),
      (state: NotificationsState) => {
        return { ...state, meta: { ...state.meta, desktopPermission: false } };
      },
    )
    .addCase(
      notificationAdded.toString(),
      (state: NotificationsState, action: any) => {
        const { notification: newNotification } = action.payload;
        let currentNotifications = state.currentNotifications;

        const notificationIndex = currentNotifications.findIndex(
          (currentNotification) =>
            currentNotification.id === newNotification.id,
        );

        if (newNotification.showInpage === true) {
          if (notificationIndex !== -1) {
            // notifications should be unique by id
            currentNotifications = currentNotifications.map(
              (currentNotification, index) =>
                index === notificationIndex
                  ? newNotification
                  : currentNotification,
            );
          } else {
            currentNotifications = [...currentNotifications, newNotification];
          }
        }

        return { ...state, currentNotifications };
      },
    )
    .addCase(
      notificationHistoryAdd.toString(),
      (state: NotificationsState, action: any) => {
        const { notification } = action.payload;
        let notificationsHistory = state.notificationsHistory;

        // We do not save new message notifications.
        if (notification.type?.indexOf('new-message-') > -1) {
          return state;
        }

        const notificationIndex = notificationsHistory.findIndex(
          (historyNotification) => historyNotification.id === notification.id,
        );

        const newNotification = Object.assign({}, notification, {
          showInDropdown: true,
        });

        if (
          notification.showInpage === true ||
          notification.showDesktop === true
        ) {
          if (notificationIndex !== -1) {
            // notifications should be unique by id
            notificationsHistory = notificationsHistory.map(
              (historyNotification, index) =>
                index === notificationIndex
                  ? newNotification
                  : historyNotification,
            );
          } else {
            notificationsHistory = [...notificationsHistory, newNotification];
          }
        }
        return { ...state, notificationsHistory };
      },
    )
    .addCase(
      notificationHistoryReset.toString(),
      (state: NotificationsState) => {
        const notificationsHistory = state.notificationsHistory;
        const historyNotification: NotificationHistoryType[] = [];
        for (const n of notificationsHistory) {
          historyNotification.push(
            Object.assign({}, n, { showInDropdown: false, unseen: false }),
          );
        }
        return { ...state, notificationsHistory: historyNotification };
      },
    )
    .addCase(
      notificationRemoveInStateById.toString(),
      (state: NotificationsState, action: any) => {
        const { id } = action.payload;
        if (id) {
          return {
            ...state,
            currentNotifications: state.currentNotifications.filter(
              (n: NotificationT) => n.id !== id,
            ),
            ratingNotificaions: state.ratingNotificaions.filter(
              (n: NotificationT) => n.id !== id,
            ),
          };
        }
        return state;
      },
    )
    .addCase(
      notificationRemoveAllById.toString(),
      (state: NotificationsState, action: any) => {
        const { id } = action.payload;
        if (id) {
          return {
            ...state,
            currentNotifications: state.currentNotifications.filter(
              (n: NotificationT) => n.id !== id,
            ),
            ratingNotificaions: state.ratingNotificaions.filter(
              (n: NotificationT) => n.id !== id,
            ),
            notificationsHistory: state.notificationsHistory.filter(
              (n: NotificationT) => n.id !== id,
            ),
          };
        }

        return state;
      },
    )
    .addCase(
      notificationRemoveInStateByType.toString(),
      (state: NotificationsState, action: any) => {
        const { type } = action.payload;
        if (type) {
          return {
            ...state,
            currentNotifications: state.currentNotifications.filter(
              (n: NotificationT) => n.type !== type,
            ),
          };
        }
        return state;
      },
    )
    .addCase(
      notificationRatingAdded.toString(),
      (state: NotificationsState, action: any) => {
        const { notification: newNotification } = action.payload;
        let ratingNotificaions = state.ratingNotificaions;

        const notificationIndex = ratingNotificaions.findIndex(
          (ratingNotification) => ratingNotification.id === newNotification.id,
        );

        if (newNotification.showInpage === true) {
          if (notificationIndex !== -1) {
            // notifications should be unique by id
            ratingNotificaions = ratingNotificaions.map(
              (ratingNotification, index) =>
                index === notificationIndex
                  ? newNotification
                  : ratingNotification,
            );
          } else {
            ratingNotificaions = [...ratingNotificaions, newNotification];
          }
        }

        return { ...state, ratingNotificaions };
      },
    )
    .addCase(
      notificationGetPersistedSucceeded.toString(),
      (
        state: NotificationsState,
        action: {
          type: string;
          payload: NotificationGetPersistedSucceededPayloadT;
        },
      ) => {
        const {
          payload: { data },
        } = action;
        const { notifications } = data;

        const persistedNotifications: Array<PersistedNotificationT> =
          notifications
            .map((notification: PersistedNotificationResponseT) => {
              try {
                return {
                  notificationId: notification.notification_id,
                  createdAt: notification.created_at,
                  isRead: notification.is_read,
                  data: JSON.parse(notification.payload),
                };
              } catch (error) {
                console.warn('Could not parse notification payload', error);
                return false;
              }
            })
            .filter(Boolean) as any[];
        const bulkIds = persistedNotifications.map(
          (notification: PersistedNotificationT) =>
            notification.data.params.bulk_id,
        );

        const localNotifications = state.notificationsHistory.filter(
          (local: NotificationHistoryType) => bulkIds.indexOf(local.id) === -1,
        );

        return {
          ...state,
          persistedNotifications,
          notificationsHistory: localNotifications,
        };
      },
    );
});

export default notifications;
